import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaWhatsapp } from 'react-icons/fa';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = "2349152474245";

const CRYPTO_OPTIONS = [
  { value: 'BTC', label: 'Bitcoin (BTC)' },
  { value: 'ETH', label: 'Ethereum (ETH)' },
  { value: 'USDT', label: 'Tether (USDT)' },
];

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'cash', label: 'Cash' },
];

export default function TradeForm() {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    crypto_type: 'BTC',
    amount: '',
    wallet_address: '',
    payment_method: 'bank_transfer',
    trade_type: 'buy',
  });
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/rates`);
        const ratesMap = {};
        response.data.rates.forEach(rate => {
          ratesMap[rate.symbol] = rate;
        });
        setRates(ratesMap);
      } catch (error) {
        console.error('Failed to fetch rates:', error);
      }
    };
    fetchRates();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculateTotal = () => {
    const rate = rates[formData.crypto_type];
    if (!rate || !formData.amount) return 0;
    
    const price = formData.trade_type === 'buy' ? rate.buy_rate : rate.sell_rate;
    return price * parseFloat(formData.amount || 0);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const generateWhatsAppMessage = () => {
    const total = calculateTotal();
    const message = `Hello OX Olorire Exchange!

I want to *${formData.trade_type.toUpperCase()}* cryptocurrency:

📋 *Trade Details:*
• Name: ${formData.full_name}
• Email: ${formData.email}
• Crypto: ${formData.crypto_type}
• Amount: ${formData.amount} ${formData.crypto_type}
• Total: ${formatPrice(total)}
• Wallet: ${formData.wallet_address}
• Payment: ${formData.payment_method.replace('_', ' ')}

Please confirm this trade. Thank you!`;
    
    return encodeURIComponent(message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Submit to backend
      await axios.post(`${API_URL}/api/trade-request`, formData);
      
      toast.success('Trade request submitted! Opening WhatsApp...');
      setSubmitted(true);

      // Open WhatsApp with pre-filled message
      setTimeout(() => {
        window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${generateWhatsAppMessage()}`, '_blank');
      }, 1000);

    } catch (error) {
      console.error('Failed to submit trade request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      crypto_type: 'BTC',
      amount: '',
      wallet_address: '',
      payment_method: 'bank_transfer',
      trade_type: 'buy',
    });
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card-gold p-8 text-center"
        data-testid="trade-success"
      >
        <CheckCircle size={64} className="mx-auto text-accent-success mb-4" />
        <h3 className="font-heading text-2xl font-semibold text-text-primary mb-2">
          Request Submitted!
        </h3>
        <p className="text-text-secondary mb-6">
          Your trade request has been sent. Please continue the conversation on WhatsApp.
        </p>
        <button
          onClick={resetForm}
          className="btn-gold"
          data-testid="new-trade-btn"
        >
          Submit Another Trade
        </button>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card-gold p-6 md:p-8"
      data-testid="trade-form"
    >
      <h3 className="font-heading text-2xl font-semibold text-text-primary mb-6">
        Submit Trade Request
      </h3>

      {/* Trade Type Toggle */}
      <div className="flex gap-4 mb-6">
        <button
          type="button"
          onClick={() => setFormData(prev => ({ ...prev, trade_type: 'buy' }))}
          className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
            formData.trade_type === 'buy'
              ? 'bg-accent-success text-white'
              : 'bg-background text-text-secondary hover:bg-border'
          }`}
          data-testid="trade-type-buy"
        >
          Buy Crypto
        </button>
        <button
          type="button"
          onClick={() => setFormData(prev => ({ ...prev, trade_type: 'sell' }))}
          className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
            formData.trade_type === 'sell'
              ? 'bg-accent-danger text-white'
              : 'bg-background text-text-secondary hover:bg-border'
          }`}
          data-testid="trade-type-sell"
        >
          Sell Crypto
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Full Name */}
        <div className="md:col-span-2">
          <label className="block text-text-secondary text-sm mb-2">Full Name</label>
          <input
            type="text"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            required
            className="input-dark w-full"
            placeholder="Enter your full name"
            data-testid="input-full-name"
          />
        </div>

        {/* Email */}
        <div className="md:col-span-2">
          <label className="block text-text-secondary text-sm mb-2">Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="input-dark w-full"
            placeholder="your@email.com"
            data-testid="input-email"
          />
        </div>

        {/* Crypto Type */}
        <div>
          <label className="block text-text-secondary text-sm mb-2">Cryptocurrency</label>
          <select
            name="crypto_type"
            value={formData.crypto_type}
            onChange={handleChange}
            className="input-dark w-full"
            data-testid="select-crypto-type"
          >
            {CRYPTO_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Amount */}
        <div>
          <label className="block text-text-secondary text-sm mb-2">Amount</label>
          <input
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            required
            min="0"
            step="any"
            className="input-dark w-full"
            placeholder="0.00"
            data-testid="input-amount"
          />
        </div>

        {/* Wallet Address */}
        <div className="md:col-span-2">
          <label className="block text-text-secondary text-sm mb-2">
            {formData.trade_type === 'buy' ? 'Your Wallet Address (to receive crypto)' : 'Your Wallet Address (to send crypto from)'}
          </label>
          <input
            type="text"
            name="wallet_address"
            value={formData.wallet_address}
            onChange={handleChange}
            required
            className="input-dark w-full font-mono text-sm"
            placeholder="Enter your wallet address"
            data-testid="input-wallet-address"
          />
        </div>

        {/* Payment Method */}
        <div className="md:col-span-2">
          <label className="block text-text-secondary text-sm mb-2">Payment Method</label>
          <select
            name="payment_method"
            value={formData.payment_method}
            onChange={handleChange}
            className="input-dark w-full"
            data-testid="select-payment-method"
          >
            {PAYMENT_METHODS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Total Estimate */}
      {formData.amount && rates[formData.crypto_type] && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-6 p-4 bg-background rounded-lg border border-border"
        >
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Estimated Total:</span>
            <span className="font-heading text-2xl font-bold gold-gradient-text">
              {formatPrice(calculateTotal())}
            </span>
          </div>
          <p className="text-text-muted text-xs mt-1">
            Rate: {formatPrice(formData.trade_type === 'buy' 
              ? rates[formData.crypto_type]?.buy_rate 
              : rates[formData.crypto_type]?.sell_rate)} per {formData.crypto_type}
          </p>
        </motion.div>
      )}

      {/* Submit Button */}
      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <motion.button
          type="submit"
          disabled={loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="btn-gold flex-1 flex items-center justify-center gap-2"
          data-testid="submit-trade-btn"
        >
          {loading ? (
            <span className="animate-pulse">Processing...</span>
          ) : (
            <>
              <Send size={18} />
              Submit Trade Request
            </>
          )}
        </motion.button>
        
        <motion.button
          type="button"
          onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${generateWhatsAppMessage()}`, '_blank')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="btn-whatsapp flex items-center justify-center gap-2"
          data-testid="whatsapp-trade-btn"
        >
          <FaWhatsapp size={20} />
          Trade via WhatsApp
        </motion.button>
      </div>

      <p className="text-text-muted text-xs mt-4 text-center">
        By submitting, you agree to our terms of service. We'll contact you via WhatsApp to complete the trade.
      </p>
    </motion.form>
  );
}
